/**
 * API 客户端与格式化函数。
 * 前端一律使用相对路径 /api/*：开发期由 Vite proxy 转发到 22670（无 CORS 问题），
 * 生产期由反向代理按子域路由 —— 同一个前端代码不改。
 */

export type Confidence = 'actual' | 'forecast' | 'mock';

export interface ApiMeta {
  /** 数据来源，渲染为面板上的来源说明 */
  source: string;
  /** 数据截至日期 YYYY-MM-DD */
  asOf: string;
  /** 是否为参考/估算数据 */
  isMock: boolean;
  /** actual 实际 / forecast 预测 / mock 参考 */
  confidence: Confidence;
}

export interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
  meta: ApiMeta | null;
}

/** 当前语言：由 I18nProvider 同步到 <html lang>，据此向 API 声明 Accept-Language，
 *  使服务端返回的提示文案（如「令牌已使用」）与界面语言一致。 */
function acceptLanguage(): string {
  if (typeof document === 'undefined') return 'zh';
  return document.documentElement.lang?.toLowerCase().startsWith('en') ? 'en' : 'zh';
}

async function handle<T>(res: Response, path: string): Promise<ApiResponse<T>> {
  let body: ApiResponse<T>;
  try {
    body = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new Error(`响应不是 JSON：${res.status} ${path}`);
  }
  // 非 2xx 或业务码非 0 一律抛错；错误信息优先用服务端 message（便于显示"必须同意隐私政策"等）
  if (!res.ok || body.code !== 0) {
    throw new Error(body?.message || `请求失败：${res.status} ${path}`);
  }
  return body;
}

export async function apiGet<T>(path: string): Promise<ApiResponse<T>> {
  const res = await fetch(path, {
    headers: { Accept: 'application/json', 'Accept-Language': acceptLanguage() },
  });
  return handle<T>(res, path);
}

export async function apiPost<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
  const res = await fetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'Accept-Language': acceptLanguage(),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return handle<T>(res, path);
}

/**
 * 读取种子文档（apps/<site>/data/*.json 的原文镜像）。
 *
 * 展示型数据（短剧作品、直播 GMV、站点文案等）不走类型化端点，而是直读文档镜像，
 * 好处是：前端拿到的与 JSON 种子逐字节一致，新增字段无需改后端。
 * 文档自带 _meta，直接作为数据口径返回。
 */
export interface DocMeta {
  source: string;
  asOf: string;
  isMock: boolean;
  confidence: Confidence;
}

export async function fetchDocument<T extends { _meta: DocMeta }>(
  site: string,
  file: string,
): Promise<{ data: T; meta: DocMeta }> {
  const path = `/api/sites/${site}/${file}`;
  const res = await fetch(path, {
    headers: { Accept: 'application/json', 'Accept-Language': acceptLanguage() },
  });
  if (!res.ok) throw new Error(`文档加载失败：${res.status} ${site}/${file}`);
  const data = (await res.json()) as T;
  if (!data?._meta) throw new Error(`文档缺少 _meta 口径标注：${site}/${file}`);
  return { data, meta: data._meta };
}

export function formatFixed(v: number, digits = 1): string {
  return v.toFixed(digits);
}
